import * as THREE from "three";
import { GameScene, themeFor } from "./Scene";
import { Player } from "./Player";
import { World } from "./World";
import { Path } from "./Path";
import { buildCoinMesh, buildGateMesh, buildSpawnPlan } from "./Spawner";
import { isFallingLog, type ObstacleContact, type ObstacleController } from "./obstacles";
import { Ghost } from "./Ghost";
import { Input } from "./Input";
import { LANE_POSITIONS, RUNNER } from "@shared/constants";
import { upgradeEffect } from "@shared/upgrades";
import { sfx } from "../audio";
import type {
  GameConfig,
  GameEventHandler,
  RunOutcome,
  RunState,
  SpawnedCoin,
  WordGate,
} from "../types";
import type { GameSettings } from "../../local/gameSettings";

export class Game {
  private scene: GameScene;
  private world: World;
  private player: Player;
  private input: Input;
  private path: Path;

  private obstacles: ObstacleController[] = [];
  private coins: SpawnedCoin[] = [];
  private gates: WordGate[] = [];

  private coinMeshes = new Map<number, THREE.Object3D>();
  private gateMeshes = new Map<number, THREE.Object3D>();

  private wordOutcomes: { wordId: string; correct: boolean }[] = [];
  private runState: RunState;
  private rafId = 0;
  private lastFrame = 0;
  private disposed = false;
  private suspendedSpeed: number | null = null;

  // Camera shake.
  private shakeUntil = 0;
  private shakeStart = 0;
  private shakeAmp = 0;
  // Slow-mo + death sequence.
  private timeScale = 1;
  private dying = false;
  private deathStartedAt = 0;
  private pendingDeath: RunOutcome | null = null;
  // Falling off the cloud course: drop for a beat, then respawn on the path.
  private falling = false;
  private fallStartedAt = 0;
  private fallRespawnAtS = 0;
  private fallPos = new THREE.Vector3();
  private fallVel = new THREE.Vector3();
  private fallAngularVel = new THREE.Vector3();
  private supportRaycaster = new THREE.Raycaster();
  private supportMeshes: THREE.Object3D[] = [];
  private hasStandableSupport = false;

  // Upgrade-driven values.
  private coinMagnetRadius = 0.7;
  private coinPickupValue = 1;
  private quizCorrectCoins = 5;
  private quizBonusCoins = 0;
  private winnerChestCoins = 0;
  private extraInvulnerableMs = 0;
  private speedBoostUntil = 0;
  private speedBoostStartedAt = 0;
  private knockSlowUntil = 0;
  private playerKnockbackSpeed = 0;
  private jumpCarrySpeed = 0;
  private wasAirborne = false;

  // Multiplayer ghost opponents (id → Ghost).
  private ghosts = new Map<string, Ghost>();
  private ghostStates = new Map<string, string>();
  private ghostContactUntil = new Map<string, number>();
  // In-world start countdown (3, 2, 1, GO) rendered inside the Three.js scene.
  private countdownGroup: THREE.Group | null = null;
  private countdownSprite: THREE.Sprite | null = null;
  private countdownRings: THREE.Object3D[] = [];
  private countdownLabel: string | null = null;
  private countdownChangedAt = 0;
  private flyoverStartedAt = 0;
  private flyoverDurationMs = 7200;
  private moveAxis = 0;
  private forwardAxis = 0;
  private lookAxisX = 0;
  private lookAxisY = 0;
  private cameraYawOffset = 0;
  private cameraPitchOffset = 0;
  private localMoveForward = 0;
  private localMoveLateral = 0;

  /** MP: feed in the latest network position for an opponent. */
  setRemotePosition(id: string, s: number, laneOffset: number, state = "run") {
    const g = this.ghosts.get(id);
    if (g) {
      g.setTarget(s, laneOffset);
      this.ghostStates.set(id, state);
    }
  }

  /** Show/update the in-world arcade countdown. Pass null to hide it. */
  setCountdownLabel(label: string | null) {
    if (this.countdownLabel === label) return;
    this.countdownLabel = label;
    this.countdownChangedAt = performance.now();
    if (!label) {
      if (this.countdownGroup) this.countdownGroup.visible = false;
      return;
    }
    if (this.runState.distance < 0.05) {
      this.runState.startedAt = performance.now();
      this.emitState();
    }
    sfx.countdown(label);
    this.ensureCountdownGroup();
    if (!this.countdownGroup || !this.countdownSprite) return;
    this.countdownGroup.visible = true;
    this.countdownSprite.material.map?.dispose();
    this.countdownSprite.material.map = makeCountdownTexture(label);
    this.countdownSprite.material.needsUpdate = true;
  }

  constructor(
    private canvas: HTMLCanvasElement,
    private config: GameConfig,
    private onEvent: GameEventHandler,
  ) {
    const theme = themeFor(config.theme, config.timeOfDay);
    this.scene = new GameScene(canvas, theme);
    this.path = new Path(config.courseLength, config.seed);
    this.world = new World(theme, config.courseLength, this.path);
    this.scene.scene.add(this.world.group);

    this.player = new Player(config.avatar);
    this.player.movementMode = config.movementMode ?? "free";
    this.player.path = this.path;
    const u = config.upgrades ?? {};
    this.player.doubleJumpUnlocked = false;
    this.coinMagnetRadius = 0.7 + upgradeEffect.coinMagnetRadius(u);
    this.coinPickupValue = Math.max(1, Math.floor(config.coinPickupValue ?? 1));
    this.quizCorrectCoins = Math.max(1, Math.floor(config.quizCorrectCoins ?? 5));
    this.quizBonusCoins = upgradeEffect.quizBonusCoins(u);
    this.winnerChestCoins = upgradeEffect.winnerChestCoins(u);
    this.extraInvulnerableMs = upgradeEffect.safetyBubbleMs(u);
    if (config.mySlotIdx !== undefined) {
      const slot = config.mySlotIdx;
      const sign = slot % 2 === 0 ? 1 : -1;
      this.player.slotOffset = sign * (Math.floor(slot / 2) + 1) * 1.35;
    }
    this.scene.scene.add(this.player.group);
    this.player.s = 0;
    this.player.update(0, performance.now());

    // Spawn ghosts for remote players.
    if (config.remotePlayers) {
      for (const rp of config.remotePlayers) {
        const g = new Ghost(rp.id, rp.displayName, rp.avatarConfig, rp.slotIdx, this.path);
        this.ghosts.set(rp.id, g);
        this.scene.scene.add(g.group);
      }
    }
    const plan = buildSpawnPlan({
      seed: config.seed,
      courseLength: this.path.length,
      objectsCount: config.objectsCount,
      coinsCount: config.coinsCount,
      words: config.words,
      questionsCount: config.questionsCount,
      path: this.path,
    });
    this.obstacles = plan.obstacles;
    this.coins = plan.coins;
    this.gates = plan.gates;

    for (const o of this.obstacles) {
      this.scene.scene.add(o.mesh);
      if (isFallingLog(o)) {
        o.onImpact = () => this.shake(performance.now(), 360, 0.5);
      }
    }
    for (const c of this.coins) {
      const m = buildCoinMesh(c, this.path);
      this.scene.scene.add(m);
      this.coinMeshes.set(c.id, m);
    }
    for (const g of this.gates) {
      const m = buildGateMesh(g, this.path);
      this.scene.scene.add(m);
      this.gateMeshes.set(g.id, m);
    }
    this.rebuildSupportMeshes();

    this.runState = {
      hearts: RUNNER.startingHearts,
      stumbles: 0,
      coins: 0,
      score: 0,
      startedAt: performance.now(),
      finishedAt: null,
      distance: 0,
      paused: !!config.startPaused,
      speed: 0,
      invulnerableUntil: 0,
    };

    this.input = new Input(canvas, {
      onLeft: () => {},
      onRight: () => {},
      onDodgeLeft: () => {},
      onDodgeRight: () => {},
      onMoveAxis: (axis) => {
        this.moveAxis = axis;
      },
      onMoveVector: (x, y) => {
        this.moveAxis = x;
        this.forwardAxis = y;
      },
      onLookVector: (x, y) => {
        this.lookAxisX = x;
        this.lookAxisY = y;
      },
      onJump: () => this.player.jump(performance.now()),
      onSlide: () => this.player.slide(performance.now()),
      onPause: () => this.togglePause(),
    }, {
      movementSensitivity: config.movementSensitivity,
      cameraSensitivity: config.cameraSensitivity,
      controllerDeadzone: config.controllerDeadzone,
    });

    this.handleResize();
    window.addEventListener("resize", this.handleResize);
    this.lastFrame = performance.now();
    if (config.startCameraFlyover) this.flyoverStartedAt = this.lastFrame;
    this.tick(this.lastFrame);
  }

  private ensureCountdownGroup() {
    if (this.countdownGroup) return;
    const group = new THREE.Group();
    group.visible = false;

    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffd93d,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });
    const ringA = new THREE.Mesh(new THREE.TorusGeometry(2.55, 0.055, 8, 80), ringMat.clone());
    const ringB = new THREE.Mesh(new THREE.TorusGeometry(1.95, 0.035, 8, 80), ringMat.clone());
    ringA.position.z = -0.04;
    ringB.position.z = -0.02;
    group.add(ringA, ringB);
    this.countdownRings = [ringA, ringB];

    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: makeCountdownTexture("3"),
        transparent: true,
        depthTest: true,
        depthWrite: false,
      }),
    );
    sprite.scale.set(5.8, 3.0, 1);
    sprite.renderOrder = 50;
    group.add(sprite);

    this.countdownSprite = sprite;
    this.countdownGroup = group;
    this.scene.scene.add(group);
  }

  private handleResize = () => {
    const r = this.canvas.getBoundingClientRect();
    this.scene.resize(r.width, r.height);
  };

  togglePause() {
    this.runState.paused = !this.runState.paused;
    this.emitState();
  }

  setMoveVector(x: number, y: number) {
    this.moveAxis = THREE.MathUtils.clamp(x, -1, 1);
    this.forwardAxis = THREE.MathUtils.clamp(y, -1, 1);
    this.input.setMoveVector(this.moveAxis, this.forwardAxis);
  }

  jump() {
    this.input.jump();
  }

  updateSettings(settings: Pick<GameSettings, "movementMode" | "movementSensitivity" | "cameraSensitivity" | "controllerDeadzone">) {
    this.player.movementMode = settings.movementMode;
    this.input.setOptions({
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
    });
  }

  setPaused(paused: boolean) {
    const wasPaused = this.runState.paused;
    this.runState.paused = paused;
    if (wasPaused && !paused && this.runState.distance < 0.05) {
      this.runState.startedAt = performance.now();
    }
    this.emitState();
  }

  private emitState() {
    this.onEvent({ type: "state", state: { ...this.runState } });
  }

  private currentSpeed(now = performance.now(), alignment = 1): number {
    // Per-level fixed speed (set by LessonPlay from lesson order index).
    const base = (this.config.runSpeed ?? RUNNER.baseSpeed) * 0.72;
    const input = this.localMoveForward;
    const boostT = (now - this.speedBoostStartedAt) / Math.max(1, this.speedBoostUntil - this.speedBoostStartedAt);
    const boostPulse = now < this.speedBoostUntil ? Math.sin(THREE.MathUtils.clamp(boostT, 0, 1) * Math.PI) : 0;
    const boost = 1 + boostPulse * 0.62;
    if (now < this.knockSlowUntil) return base * input * alignment * 0.12;
    return base * input * alignment * boost;
  }

  private tick = (now: number) => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.tick);
    const rawDt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    if (this.dying) {
      const t = Math.min(1, (now - this.deathStartedAt) / 600);
      this.timeScale = 1 - 0.85 * easeOutCubic(t);
      const dt = rawDt * this.timeScale;
      this.player.path = null; // free pieces from path during crumble
      this.player.update(dt, now);
      for (const g of this.ghosts.values()) g.update(rawDt);
      const spectateTarget = this.config.spectateAfterDeath && now - this.deathStartedAt > 2600
        ? this.pickSpectateGhost()
        : null;
      if (spectateTarget) this.applySpectatorCamera(spectateTarget, now);
      else this.applyDeathCamera(now);
      this.scene.render();
      if (now - this.deathStartedAt > 1500 && this.pendingDeath) {
        this.onEvent({ type: "death", outcome: this.pendingDeath });
        this.pendingDeath = null;
      }
      return;
    }
    if (this.falling) {
      this.updateFalling(rawDt, now);
      this.updateCountdown(now);
      this.scene.render();
      return;
    }
    if (!this.runState.paused && this.runState.finishedAt == null) {
      this.update(rawDt, now);
    } else {
      this.applyCamera(this.runState.distance, now);
    }
    this.updateCountdown(now);
    this.scene.render();
  };

  private update(dt: number, now: number) {
    const playerKnockedDown = this.player.state === "hit";
    const playerAirborne = this.player.y > 0.16 || this.player.state === "jump";
    if (playerAirborne && !this.wasAirborne) this.jumpCarrySpeed = this.runState.speed;
    this.updateDesiredPlayerFacing(playerAirborne);
    const controlScale = playerKnockedDown || playerAirborne ? 0 : 1;
    const moveAlignment = this.player.getMovementAlignment();
    if (playerKnockedDown) {
      this.runState.speed = this.playerKnockbackSpeed;
      this.playerKnockbackSpeed *= Math.exp(-1.85 * dt);
      if (Math.abs(this.playerKnockbackSpeed) < 0.05) this.playerKnockbackSpeed = 0;
    } else {
      this.playerKnockbackSpeed = 0;
      this.runState.speed = playerAirborne ? this.jumpCarrySpeed : this.currentSpeed(now, moveAlignment);
    }
    this.runState.distance = THREE.MathUtils.clamp(
      this.runState.distance + this.runState.speed * dt,
      0,
      this.path.length,
    );
    const z = this.runState.distance;
    this.player.s = z;
    this.player.steer(this.localMoveLateral * moveAlignment * controlScale, dt);
    this.player.setMoveIntensity(Math.min(1, Math.hypot(this.moveAxis, this.forwardAxis)) * moveAlignment * controlScale);
    this.player.update(dt, now);
    this.wasAirborne = this.player.y > 0.16 || this.player.state === "jump";
    if (!this.wasAirborne) this.jumpCarrySpeed = 0;
    this.resolveStandableSurface();
    if (this.shouldFallOffCourse()) {
      this.beginFall(now);
      return;
    }
    this.applyCamera(z, now);

    // Coin pickups (radius bumped by Coin Magnet upgrade).
    for (const c of this.coins) {
      if (c.collected) continue;
      const inLane = this.player.movementMode === "free"
        ? this.player.overlapsLane(c.lane, 0.85)
        : c.lane === this.player.laneTarget;
      const inRadius = Math.abs(c.z - z) < this.coinMagnetRadius;
      const magnet = this.coinMagnetRadius > 0.7;
      const laneOk = inLane || (magnet && this.player.overlapsLane(c.lane, 2.4));
      if (inRadius && laneOk) {
        c.collected = true;
        const m = this.coinMeshes.get(c.id);
        if (m) m.visible = false;
        this.runState.coins += this.coinPickupValue;
        this.runState.score += 10;
        sfx.coin();
        this.onEvent({ type: "coin", value: this.coinPickupValue });
        this.emitState();
      }
    }

    // Update obstacles within visible window.
    for (const o of this.obstacles) {
      const oz = o.getZ();
      if (oz < z - 8 || oz > z + 60) continue;
      o.update({ dt, now, playerZ: z });
    }
    this.updateDynamicObstacleContacts();
    if (this.resolvePhysicalObstacleContacts(now)) return;
    if (this.path.length - z < 80) this.world.update(now);

    // Update opponent ghosts.
    for (const g of this.ghosts.values()) g.update(dt);
    if (this.player.movementMode === "free") {
      const myLateral = this.player.laneOffset + this.player.slotOffset;
      for (const g of this.ghosts.values()) {
        if (this.ghostStates.get(g.id) === "finished") continue;
        if ((this.ghostContactUntil.get(g.id) ?? 0) > now) continue;
        if (Math.abs(g.s - z) > 0.95) continue;
        const otherLateral = g.laneOffset + g.slotOffset;
        const overlap = 0.78 - Math.abs(otherLateral - myLateral);
        if (overlap <= 0) continue;
        const dir = myLateral >= otherLateral ? 1 : -1;
        this.player.applyImpulse(dir * THREE.MathUtils.clamp(overlap * 10, 3.5, 8.5), 1.2);
        this.ghostContactUntil.set(g.id, now + 260);
        this.shake(now, 120, 0.08);
        break;
      }
    }

    const jumping = this.player.y > 0.5;
    const sliding = this.player.state === "slide";
    for (const o of this.obstacles) {
      if (o.resolved) continue;
      if (o.getContact) continue;
      const oz = o.getZ();
      if (Math.abs(oz - z) > obstacleContactDepth(o)) continue;
      const laneHit = this.player.movementMode === "free"
        ? o.lanes().some((lane) => this.player.overlapsLane(lane, obstacleLaneRadius(o)))
        : o.lanes().includes(this.player.laneTarget);
      if (!laneHit) continue;
      if (o.passes({ jumping, sliding })) {
        o.resolved = true;
        continue;
      }

      const forceImpact = !!o.hasForceImpact?.();
      if (forceImpact && now < this.runState.invulnerableUntil) continue;
      this.blockPlayerAtObstacle(o);
      if (!forceImpact) return;

      this.runState.stumbles += 1;
      this.runState.score = Math.max(0, this.runState.score - 15);
      this.runState.invulnerableUntil = now + obstacleRecoveryMs(o) + this.extraInvulnerableMs;
      const obstacleLateral = obstacleCenterOffset(o);
      const playerLateral = this.player.laneOffset;
      const dir = playerLateral >= obstacleLateral ? 1 : -1;
      const impulse = obstacleImpactImpulse(o);
      const massScale = Math.sqrt(Math.max(1, o.mass ?? 3) / 3);
      this.player.knockdown(now, dir * impulse.lateral * massScale, impulse.vertical * Math.min(1.35, massScale));
      this.playerKnockbackSpeed = -impulse.forward * Math.min(1.45, massScale);
      this.knockSlowUntil = now + impulse.slowMs;
      o.applyPhysicsImpulse?.(-Math.abs(this.playerKnockbackSpeed) * 0.08, -dir * impulse.lateral * 0.14);
      o.impact?.({
        now,
        playerS: z,
        playerLaneOffset: playerLateral,
        playerForwardVelocity: this.runState.speed,
      });
      sfx.hit();
      this.shake(now, impulse.shakeMs, impulse.shake);
      this.onEvent({ type: "hit" });
      this.emitState();
      return;
    }

    if (z >= this.path.length) {
      this.finishRun(true);
      return;
    }

    // Word gates.
    for (const g of this.gates) {
      if (g.triggered) continue;
      if (g.z - z < 0 && Math.abs(g.z - z) < 1.5) {
        g.triggered = true;
        this.suspend();
        this.onEvent({
          type: "challenge",
          word: g.word,
          resolve: (correct) => this.resolveChallenge(g.word.id, correct),
        });
      }
    }

    // Coin spin — budget the nearest visible coins only.
    let coinSpinBudget = 24;
    for (const c of this.coins) {
      if (c.collected) continue;
      if (coinSpinBudget <= 0) break;
      if (c.z < z - 4 || c.z > z + 50) continue;
      const m = this.coinMeshes.get(c.id);
      if (m) m.rotation.y += dt * 4;
      coinSpinBudget--;
    }

  }

  private suspend() {
    this.suspendedSpeed = this.runState.speed;
    this.runState.paused = true;
    this.emitState();
  }

  private resume() {
    if (this.suspendedSpeed != null) this.runState.speed = this.suspendedSpeed;
    this.suspendedSpeed = null;
    this.runState.paused = false;
    this.emitState();
  }

  private blockPlayerAtObstacle(o: ObstacleController) {
    this.runState.distance = Math.max(0, o.getZ() - obstacleBlockDistance(o));
    this.runState.speed = 0;
    this.player.s = this.runState.distance;
  }

  private updateDesiredPlayerFacing(playerAirborne = false) {
    const x = this.moveAxis;
    const y = this.forwardAxis;
    const amount = Math.min(1, Math.hypot(x, y));
    if (amount < 0.05 || this.player.state === "hit" || playerAirborne) {
      this.localMoveForward = 0;
      this.localMoveLateral = 0;
      this.player.setDesiredFacing(null, 0);
      return;
    }
    const cameraForward = _moveCameraForward.set(0, 0, -1).applyQuaternion(this.scene.camera.quaternion);
    cameraForward.y = 0;
    if (cameraForward.lengthSq() < 0.001) cameraForward.set(0, 0, 1);
    cameraForward.normalize();
    const cameraRight = _moveCameraRight.set(1, 0, 0).applyQuaternion(this.scene.camera.quaternion);
    cameraRight.y = 0;
    if (cameraRight.lengthSq() < 0.001) cameraRight.set(1, 0, 0);
    cameraRight.normalize();
    const worldDir = _moveWorldDir
      .copy(cameraForward)
      .multiplyScalar(y)
      .addScaledVector(cameraRight, x);
    if (worldDir.lengthSq() < 0.001) {
      this.localMoveForward = 0;
      this.localMoveLateral = 0;
      this.player.setDesiredFacing(null, 0);
      return;
    }
    worldDir.normalize();
    const tangent = this.path.tangentAt(this.runState.distance, _moveTan);
    const side = _moveSide.set(tangent.z, 0, -tangent.x).normalize();
    this.localMoveForward = THREE.MathUtils.clamp(worldDir.dot(tangent) * amount, -1, 1);
    this.localMoveLateral = THREE.MathUtils.clamp(worldDir.dot(side) * amount, -1, 1);
    this.player.setDesiredFacing(Math.atan2(worldDir.x, worldDir.z), amount);
  }

  private shouldFallOffCourse(): boolean {
    if (this.hasStandableSupport) return false;
    const lateral = this.player.laneOffset + this.player.slotOffset;
    if (Math.abs(lateral) > this.currentFallHalfWidth()) return true;
    return false;
  }

  private currentFallHalfWidth() {
    return this.path.narrowBridgeHalfWidthAt(this.runState.distance) ?? COURSE_FALL_HALF_WIDTH;
  }

  private beginFall(now: number) {
    if (this.falling) return;
    const tangent = this.path.tangentAt(this.runState.distance, _fallTan);
    const lateral = this.player.laneOffset + this.player.slotOffset;
    const side = _fallSide.set(tangent.z, 0, -tangent.x).normalize();
    const offSide = Math.abs(lateral) > this.currentFallHalfWidth() ? Math.sign(lateral) : Math.sign(this.player.getLateralVelocity());
    this.falling = true;
    this.fallStartedAt = now;
    this.fallRespawnAtS = THREE.MathUtils.clamp(this.runState.distance, 0, this.path.length);
    this.fallPos.copy(this.player.group.position);
    this.fallVel
      .copy(side)
      .multiplyScalar(offSide * THREE.MathUtils.clamp(Math.abs(this.player.getLateralVelocity()) * 0.38 + 2.4, 2.6, 7.8))
      .addScaledVector(tangent, THREE.MathUtils.clamp(this.playerKnockbackSpeed || this.runState.speed, -8, 8) * 0.45);
    this.fallVel.y = THREE.MathUtils.clamp(this.player.vy, -1.4, 1.6) + 0.15;
    this.fallAngularVel.set(
      THREE.MathUtils.clamp(-this.playerKnockbackSpeed * 0.55 + this.fallVel.z * 0.16, -7.5, 7.5),
      THREE.MathUtils.clamp(offSide * 1.4 + this.player.getLateralVelocity() * 0.1, -4.2, 4.2),
      THREE.MathUtils.clamp(-offSide * 5.2 - this.player.getLateralVelocity() * 0.22, -8.5, 8.5),
    );
    this.player.path = null;
    this.player.setFallPose(true);
    this.player.update(0, now);
    this.runState.speed = 0;
    this.shake(now, 180, 0.14);
  }

  private updateFalling(dt: number, now: number) {
    this.runState.speed = 0;
    this.player.setMoveIntensity(0);
    this.fallVel.y -= 9.8 * dt;
    this.fallPos.addScaledVector(this.fallVel, dt);
    this.player.group.position.copy(this.fallPos);
    this.player.group.rotation.x += this.fallAngularVel.x * dt;
    this.player.group.rotation.y += this.fallAngularVel.y * dt;
    this.player.group.rotation.z += this.fallAngularVel.z * dt;
    this.fallAngularVel.multiplyScalar(Math.exp(-0.16 * dt));

    const target = this.player.group.position;
    const fallT = THREE.MathUtils.clamp((now - this.fallStartedAt) / 3000, 0, 1);
    const horizontalFall = _fallLookDir.set(this.fallVel.x, 0, this.fallVel.z);
    if (horizontalFall.lengthSq() < 0.001) {
      this.scene.camera.getWorldDirection(horizontalFall).multiplyScalar(-1);
      horizontalFall.y = 0;
    }
    horizontalFall.normalize();
    const desired = _fallCam
      .copy(target)
      .addScaledVector(horizontalFall, 5.4)
      .addScaledVector(_worldUp, THREE.MathUtils.lerp(5.8, 8.2, fallT));
    this.scene.camera.position.lerp(desired, 0.14);
    this.scene.camera.lookAt(target.x, target.y - 1.25 - fallT * 2.4, target.z);
    this.scene.followShadows(target.x, target.z);

    if (now - this.fallStartedAt >= 3000) this.respawnFromFall(now);
  }

  private respawnFromFall(now: number) {
    this.falling = false;
    this.runState.distance = this.fallRespawnAtS;
    this.runState.invulnerableUntil = now + 1300;
    this.player.resetOnCourse(this.path, this.fallRespawnAtS, 0);
    this.player.update(0, now);
    this.applyCamera(this.runState.distance, now);
    this.emitState();
  }

  private resolvePhysicalObstacleContacts(now: number): boolean {
    const playerS = this.runState.distance;
    const playerLaneOffset = this.player.laneOffset;
    const playerRadius = this.player.getRadius();
    const playerLateralVelocity = this.player.getLateralVelocity();
    const jumping = this.player.y > 0.5;
    const sliding = this.player.state === "slide";

    for (const o of this.obstacles) {
      if (o.resolved || !o.getContact) continue;
      const oz = o.getZ();
      if (oz < playerS - 8 || oz > playerS + 60) continue;
      if (o.passes({ jumping, sliding })) continue;
      const contact = o.getContact({
        playerS,
        playerLaneOffset,
        playerY: this.player.y,
        playerRadius,
        playerForwardVelocity: this.runState.speed,
        playerLateralVelocity,
      });
      if (!contact) continue;
      this.resolveContactPenetration(contact);
      if (!contact.isForceful) {
        this.runState.speed = Math.min(this.runState.speed, 0);
        return true;
      }
      this.applyPhysicalImpact(o, contact, now);
      return true;
    }
    return false;
  }

  private resolveContactPenetration(contact: ObstacleContact) {
    const correction = Math.min(contact.penetration + 0.03, 0.42);
    if (correction <= 0) return;
    this.runState.distance = THREE.MathUtils.clamp(
      this.runState.distance + contact.normalS * correction,
      0,
      this.path.length,
    );
    this.player.s = this.runState.distance;
    const lateral = this.player.laneOffset + contact.normalL * correction;
    this.player.laneOffset = THREE.MathUtils.clamp(lateral, -COURSE_FALL_HALF_WIDTH - 0.4, COURSE_FALL_HALF_WIDTH + 0.4);
  }

  private applyPhysicalImpact(o: ObstacleController, contact: ObstacleContact, now: number) {
    const alreadyDown = this.player.state === "hit";
    const canCountHit = !alreadyDown && now >= this.runState.invulnerableUntil;
    const grounded = this.player.y <= 0.12 && this.player.state !== "jump";
    const playerMass = alreadyDown ? 2.4 : grounded ? 5.2 : 3.1;
    const obstacleMass = o.applyPhysicsImpulse ? Math.max(1, o.mass ?? 3) : 200;
    const obstacleSpeed = Math.hypot(contact.obstacleVelocityS, contact.obstacleVelocityL);
    const relNormalSpeed = Math.max(0, contact.relativeSpeed);
    const transfer = obstacleMass / (obstacleMass + playerMass);
    const massScale = Math.sqrt(obstacleMass / Math.max(1, playerMass));
    const speedScale = THREE.MathUtils.clamp(relNormalSpeed / 6, 0.45, 1.85);
    const normalImpulse = (2.8 + relNormalSpeed * 0.82) * transfer * massScale;

    // Surface drag lets a rotating bar carry the player along its sweep, while
    // the main impulse still comes from the contact normal like a rigid-body hit.
    const tangentS = -contact.normalL;
    const tangentL = contact.normalS;
    const obstacleTangentSpeed = contact.obstacleVelocityS * tangentS + contact.obstacleVelocityL * tangentL;
    const dragScale = alreadyDown ? 0.72 : grounded ? 0.3 : 0.48;
    const tangentImpulse = obstacleTangentSpeed * dragScale * transfer;
    const groundedResistance = grounded && !alreadyDown ? 0.82 : 1.08;

    const lateralImpulse = (contact.normalL * normalImpulse + tangentL * tangentImpulse) * groundedResistance;
    const forwardImpulse = (contact.normalS * normalImpulse + tangentS * tangentImpulse) * groundedResistance;
    const verticalImpulse = o.kind === "boulder"
      ? THREE.MathUtils.clamp(0.38 + relNormalSpeed * 0.07 * massScale, 0.35, 1.5)
      : THREE.MathUtils.clamp((alreadyDown ? 0.04 : 0.1) + relNormalSpeed * 0.03 * massScale, 0.04, 0.52);

    this.player.knockdown(now, lateralImpulse, verticalImpulse);
    const nextPush = alreadyDown ? this.playerKnockbackSpeed + forwardImpulse : forwardImpulse;
    this.playerKnockbackSpeed = THREE.MathUtils.clamp(nextPush, -18, 18);
    this.knockSlowUntil = now + THREE.MathUtils.clamp(500 + relNormalSpeed * 95, 700, 1650);
    o.applyPhysicsImpulse?.(
      -forwardImpulse * playerMass * 0.16,
      -lateralImpulse * playerMass * 0.16,
    );
    o.impact?.({
      now,
      playerS: this.runState.distance,
      playerLaneOffset: this.player.laneOffset,
      playerForwardVelocity: this.runState.speed,
    });

    if (canCountHit) {
      this.runState.stumbles += 1;
      this.runState.score = Math.max(0, this.runState.score - 15);
      this.runState.invulnerableUntil = now + obstacleRecoveryMs(o) + this.extraInvulnerableMs;
      this.shake(
        now,
        THREE.MathUtils.clamp(260 + relNormalSpeed * 30, 280, 680),
        THREE.MathUtils.clamp(0.25 + speedScale * 0.28, 0.28, 0.9),
      );
      this.onEvent({ type: "hit" });
      this.emitState();
    }
  }

  private updateDynamicObstacleContacts() {
    const dynamic = this.obstacles.filter((o) => !o.resolved && o.applyPhysicsImpulse && o.getLateralOffset);
    for (let i = 0; i < dynamic.length; i++) {
      for (let j = i + 1; j < dynamic.length; j++) {
        const a = dynamic[i]!;
        const b = dynamic[j]!;
        const dz = b.getZ() - a.getZ();
        const dl = b.getLateralOffset!() - a.getLateralOffset!();
        const distSq = dz * dz + dl * dl;
        if (distSq > 3.1 || distSq < 0.0001) continue;
        const dist = Math.sqrt(distSq);
        const nx = dz / dist;
        const nl = dl / dist;
        const strength = (3.1 - distSq) * 2.2;
        const aMass = a.mass ?? 3;
        const bMass = b.mass ?? 3;
        a.applyPhysicsImpulse!(-nx * strength * bMass, -nl * strength * bMass);
        b.applyPhysicsImpulse!(nx * strength * aMass, nl * strength * aMass);
      }
    }
  }

  private rebuildSupportMeshes() {
    const meshes: THREE.Object3D[] = [];
    const skip = new Set<THREE.Object3D>([this.player.group, this.countdownGroup].filter(Boolean) as THREE.Object3D[]);
    const isSkipped = (obj: THREE.Object3D) => {
      for (const root of skip) {
        let current: THREE.Object3D | null = obj;
        while (current) {
          if (current === root) return true;
          current = current.parent;
        }
      }
      return false;
    };
    this.scene.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (isSkipped(obj)) return;
      const mat = obj.material;
      const transparent = Array.isArray(mat) ? mat.some((m) => m.transparent && m.opacity < 0.75) : mat.transparent && mat.opacity < 0.75;
      if (transparent) return;
      meshes.push(obj);
    });
    this.supportMeshes = meshes;
  }

  private resolveStandableSurface() {
    this.hasStandableSupport = false;
    if (this.player.vy > 0.2 || this.player.state === "slide" || this.player.state === "hit") return;
    if (this.supportMeshes.length === 0) return;
    const groundY = this.path.heightAt(this.runState.distance);
    const footY = this.player.group.position.y + this.player.y;
    _supportOrigin.set(this.player.group.position.x, footY + 2.2, this.player.group.position.z);
    this.supportRaycaster.set(_supportOrigin, _supportDown);
    this.supportRaycaster.far = 4.8;
    const hits = this.supportRaycaster.intersectObjects(this.supportMeshes, false);
    for (const hit of hits) {
      if (hit.object === this.player.group) continue;
      if (hit.point.y <= groundY + 0.08) continue;
      if (hit.point.y > footY + 0.34) continue;
      this.hasStandableSupport = true;
      this.player.landOn(hit.point.y - groundY);
      return;
    }
    this.player.clearSurfaceSupport();
  }

  private resolveChallenge(wordId: string, correct: boolean) {
    this.wordOutcomes.push({ wordId, correct });
    if (correct) {
      this.runState.coins += this.quizCorrectCoins + this.quizBonusCoins;
      this.runState.score += 50;
      const now = performance.now();
      this.speedBoostStartedAt = now;
      this.speedBoostUntil = now + 1000;
      this.player.boost(now);
      sfx.correct();
    } else {
      this.runState.hearts = Math.max(0, this.runState.hearts - 1);
      sfx.wrong();
    }
    this.emitState();
    this.resume();
  }

  private finishRun(finished: boolean) {
    if (this.runState.finishedAt != null) return;
    this.runState.finishedAt = performance.now();
    if (finished) {
      this.runState.coins += this.winnerChestCoins;
      sfx.finish();
    }
    const outcome: RunOutcome = {
      score: this.runState.score,
      timeMs: Math.round(this.runState.finishedAt - this.runState.startedAt),
      coinsEarned: this.runState.coins,
      hearts: this.runState.hearts,
      stumbles: this.runState.stumbles,
      wordOutcomes: this.wordOutcomes,
      finished,
    };
    if (!finished) {
      this.shake(performance.now(), 700, 0.9);
      this.player.crumble();
      this.dying = true;
      this.deathStartedAt = performance.now();
      this.pendingDeath = outcome;
      return;
    }
    this.onEvent({ type: "finish", outcome });
  }

  private shake(now: number, durationMs: number, amplitude: number) {
    if (now + durationMs > this.shakeUntil) {
      this.shakeUntil = now + durationMs;
      this.shakeStart = now;
    }
    this.shakeAmp = Math.max(this.shakeAmp, amplitude);
  }

  private applyCamera(z: number, now: number) {
    if (this.flyoverStartedAt && z < 0.05 && now - this.flyoverStartedAt < this.flyoverDurationMs) {
      this.applyStartFlyover(now);
      return;
    }

    const playerPos = this.player.group.position;
    this.player.group.getWorldDirection(_camForward);
    const forward = _camForward.normalize();
    const lookActive = Math.hypot(this.lookAxisX, this.lookAxisY) > 0.04;
    if (lookActive) {
      this.cameraYawOffset = THREE.MathUtils.clamp(this.cameraYawOffset - this.lookAxisX * 0.052, -Math.PI * 0.86, Math.PI * 0.86);
      this.cameraPitchOffset = THREE.MathUtils.clamp(this.cameraPitchOffset + this.lookAxisY * 0.032, -1.05, 0.68);
    } else {
      this.cameraYawOffset *= Math.exp(-2.8 * 0.016);
      this.cameraPitchOffset *= Math.exp(-2.8 * 0.016);
    }
    _camOrbitForward.copy(forward).applyAxisAngle(_worldUp, this.cameraYawOffset).normalize();
    const desired = _camDesired
      .copy(playerPos)
      .addScaledVector(_camOrbitForward, -5.8);
    desired.y = playerPos.y + 2.65 + Math.sin(this.cameraPitchOffset) * 3.2;
    const look = _camLook.copy(playerPos).addScaledVector(_camOrbitForward, 2.7);
    look.y = playerPos.y + 0.75;
    if (now < this.shakeUntil) {
      const total = Math.max(1, this.shakeUntil - this.shakeStart);
      const remaining = (this.shakeUntil - now) / total;
      const a = this.shakeAmp * remaining;
      desired.x += (Math.random() - 0.5) * a;
      desired.y += (Math.random() - 0.5) * a;
      desired.z += (Math.random() - 0.5) * a * 0.4;
    } else {
      this.shakeAmp = 0;
    }
    this.scene.camera.position.lerp(desired, 0.28);
    this.scene.camera.lookAt(look.x, look.y, look.z);
    this.scene.followShadows(playerPos.x, playerPos.z);
  }

  private applyDeathCamera(now: number) {
    const t = Math.min(1, (now - this.deathStartedAt) / 1450);
    const p = easeInOutCubic(t);
    const target = this.player.group.position;
    const tan = this.path.tangentAt(this.runState.distance, _deathTan);
    const side = _deathSide.set(tan.z, 0, -tan.x).normalize();
    const camDistance = THREE.MathUtils.lerp(10.5, 3.4, p);
    const sideDrift = THREE.MathUtils.lerp(0, 1.25, p);
    const height = THREE.MathUtils.lerp(5.4, 2.25, p);
    const cam = _deathCam
      .copy(target)
      .addScaledVector(tan, -camDistance)
      .addScaledVector(side, sideDrift);
    cam.y = height;
    this.scene.camera.position.copy(cam);
    this.scene.camera.lookAt(target.x, 0.95 + Math.sin(t * Math.PI) * 0.25, target.z);
    this.scene.followShadows(target.x, target.z);
  }

  private pickSpectateGhost(): Ghost | null {
    let best: Ghost | null = null;
    for (const g of this.ghosts.values()) {
      const state = this.ghostStates.get(g.id);
      if (state === "finished") continue;
      if (!best || g.targetS > best.targetS) best = g;
    }
    if (best) return best;
    for (const g of this.ghosts.values()) {
      if (!best || g.targetS > best.targetS) best = g;
    }
    return best;
  }

  private applySpectatorCamera(targetGhost: Ghost, now: number) {
    const s = THREE.MathUtils.clamp(targetGhost.s, 0, this.path.length);
    const tan = this.path.tangentAt(s, _spectateTan);
    const side = _spectateSide.set(tan.z, 0, -tan.x).normalize();
    const look = this.path.positionAt(Math.min(this.path.length, s + 12), targetGhost.laneOffset + targetGhost.slotOffset, _spectateLook);
    const base = targetGhost.group.position;
    const cam = _spectateCam
      .copy(base)
      .addScaledVector(tan, -10.5)
      .addScaledVector(side, 2.4);
    cam.y = 4.8;
    const blend = Math.min(1, Math.max(0.04, (now - this.deathStartedAt - 2600) / 900));
    this.scene.camera.position.lerp(cam, blend);
    this.scene.camera.lookAt(look.x, 1.8, look.z);
    this.scene.followShadows(base.x, base.z);
  }

  private applyStartFlyover(now: number) {
    const t = Math.min(1, (now - this.flyoverStartedAt) / this.flyoverDurationMs);
    if (t < 0.76) {
      const p = easeInOutCubic(t / 0.76);
      const s = THREE.MathUtils.lerp(this.path.length - 22, 18, p);
      const lookS = THREE.MathUtils.lerp(this.path.length - 48, 0, p);
      const cam = this.path.positionAt(Math.max(0, s), 0, _flyCam);
      const look = this.path.positionAt(Math.min(this.path.length, lookS), 0, _flyLook);
      const sideDrift = Math.sin(p * Math.PI) * 5.8;
      this.scene.camera.position.set(cam.x + sideDrift, 9.7 - p * 2.4, cam.z + 8 - p * 2);
      this.scene.camera.lookAt(look.x, 1.6 + Math.sin(p * Math.PI) * 1.4, look.z);
      this.scene.followShadows(this.player.group.position.x, this.player.group.position.z);
      return;
    }

    const orbitT = easeInOutCubic((t - 0.76) / 0.24);
    const start = this.path.positionAt(0, 0, _flyStart);
    const tan = this.path.tangentAt(0, _flyTan);
    const side = _flySide.set(tan.z, 0, -tan.x).normalize();
    const angle = orbitT * Math.PI;
    const radius = THREE.MathUtils.lerp(9.0, 12.0, orbitT);
    const cam = _flyCam
      .copy(start)
      .addScaledVector(tan, Math.cos(angle) * radius)
      .addScaledVector(side, Math.sin(angle) * 5.3);
    cam.y = THREE.MathUtils.lerp(7.0, 5.25, orbitT);
    this.scene.camera.position.copy(cam);
    const look = _flyLook.copy(start).addScaledVector(tan, THREE.MathUtils.lerp(0.5, 14, orbitT));
    this.scene.camera.lookAt(look.x, 1.45, look.z);
    this.scene.followShadows(this.player.group.position.x, this.player.group.position.z);
  }

  private updateCountdown(now: number) {
    if (!this.countdownGroup || !this.countdownLabel) return;
    const isGo = this.countdownLabel === "GO!";
    const s = Math.min(this.path.length, this.runState.distance + (isGo ? 36 : 24));
    const pos = this.path.positionAt(s, 0, _countdownPos);
    this.countdownGroup.position.set(pos.x, isGo ? 7.1 : 5.0, pos.z);
    this.countdownGroup.quaternion.copy(this.scene.camera.quaternion);

    const t = Math.min(1, (now - this.countdownChangedAt) / 850);
    const pop = 1 + Math.sin((1 - t) * Math.PI) * 0.34;
    const goBoost = isGo ? 1.85 : 1.05;
    this.countdownGroup.scale.setScalar(pop * goBoost);

    for (let i = 0; i < this.countdownRings.length; i++) {
      const ring = this.countdownRings[i]!;
      ring.rotation.z += (i === 0 ? 0.055 : -0.075);
      const mat = (ring as THREE.Mesh).material as THREE.MeshBasicMaterial;
      mat.opacity = isGo ? 0.5 : 0.62 + Math.sin(now * 0.006 + i) * 0.12;
      ring.scale.setScalar(1 + i * 0.08 + Math.sin(now * 0.005 + i) * 0.04);
    }
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.handleResize);
    this.input.dispose();
    this.countdownSprite?.material.map?.dispose();
    this.scene.dispose();
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function obstacleCenterOffset(o: ObstacleController): number {
  if (o.getLateralOffset) return o.getLateralOffset();
  const lanes = o.lanes();
  if (lanes.length === 0) return 0;
  const total = lanes.reduce((sum, lane) => sum + (LANE_POSITIONS[lane] ?? 0), 0);
  return total / lanes.length;
}

function obstacleBlockDistance(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 1.35;
    case "log":
    case "spike-roller":
      return 1.25;
    case "lava":
      return 1.15;
    case "barrier":
    case "crate":
    default:
      return 0.95;
  }
}

function obstacleContactDepth(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 1.55;
    case "sweeper":
      return 1.65;
    case "lava":
    case "spike-roller":
      return 1.25;
    default:
      return 1.1;
  }
}

function obstacleLaneRadius(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 0.92;
    case "sweeper":
      return 1.12;
    case "lava":
    case "spike-roller":
      return 0.82;
    default:
      return 0.72;
  }
}

function obstacleRecoveryMs(o: ObstacleController): number {
  if (o.kind === "sweeper") return 900;
  return o.kind === "boulder" ? 620 : 520;
}

function obstacleImpactImpulse(o: ObstacleController) {
  switch (o.kind) {
    case "boulder":
      return { lateral: 5.6, vertical: 2.2, forward: 13.8, slowMs: 1050, shakeMs: 520, shake: 0.78 };
    case "sweeper":
      return { lateral: 7.4, vertical: 4.4, forward: 10.8, slowMs: 950, shakeMs: 460, shake: 0.66 };
    case "spike-roller":
    case "log":
      return { lateral: 3.1, vertical: 3.2, forward: 7.2, slowMs: 560, shakeMs: 260, shake: 0.38 };
    default:
      return { lateral: 2.25, vertical: 2.7, forward: 4.8, slowMs: 450, shakeMs: 210, shake: 0.28 };
  }
}

const COURSE_FALL_HALF_WIDTH = 7.05;
const _camDesired = new THREE.Vector3();
const _camForward = new THREE.Vector3();
const _camOrbitForward = new THREE.Vector3();
const _camLook = new THREE.Vector3();
const _countdownPos = new THREE.Vector3();
const _deathCam = new THREE.Vector3();
const _deathSide = new THREE.Vector3();
const _deathTan = new THREE.Vector3();
const _spectateCam = new THREE.Vector3();
const _spectateLook = new THREE.Vector3();
const _spectateSide = new THREE.Vector3();
const _spectateTan = new THREE.Vector3();
const _supportDown = new THREE.Vector3(0, -1, 0);
const _supportOrigin = new THREE.Vector3();
const _fallCam = new THREE.Vector3();
const _fallLookDir = new THREE.Vector3();
const _fallSide = new THREE.Vector3();
const _fallTan = new THREE.Vector3();
const _moveSide = new THREE.Vector3();
const _moveTan = new THREE.Vector3();
const _moveCameraForward = new THREE.Vector3();
const _moveCameraRight = new THREE.Vector3();
const _moveWorldDir = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);
const _flyCam = new THREE.Vector3();
const _flyLook = new THREE.Vector3();
const _flyStart = new THREE.Vector3();
const _flyTan = new THREE.Vector3();
const _flySide = new THREE.Vector3();

function makeCountdownTexture(label: string): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;
  const isGo = label === "GO!";

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(512, 260);
  ctx.rotate(isGo ? -0.04 : -0.015);
  ctx.font = `900 ${isGo ? 245 : 286}px Nunito, Arial Black, system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = isGo ? 10 : 14;
  ctx.shadowOffsetX = isGo ? 18 : 10;
  ctx.shadowOffsetY = isGo ? 28 : 18;
  ctx.lineWidth = isGo ? 30 : 38;
  ctx.strokeStyle = isGo ? "#d96c00" : "#13204b";
  ctx.strokeText(label, 0, isGo ? 8 : 0);
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = isGo ? 10 : 8;
  ctx.strokeStyle = "rgba(255,255,255,0.88)";
  ctx.strokeText(label, 0, isGo ? 8 : 0);
  const textGrad = ctx.createLinearGradient(0, -180, 0, 180);
  textGrad.addColorStop(0, "#ffffff");
  textGrad.addColorStop(0.28, isGo ? "#fff8a8" : "#fff4b8");
  textGrad.addColorStop(0.55, isGo ? "#ffd93d" : "#ffd93d");
  textGrad.addColorStop(1, isGo ? "#ff9d36" : "#ffcf45");
  ctx.fillStyle = textGrad;
  ctx.fillText(label, 0, isGo ? 8 : 0);
  ctx.restore();

  ctx.fillStyle = isGo ? "#ffffff" : "#ffd93d";
  for (const [x, y, r] of [
    [145, 130, 16],
    [858, 132, 20],
    [186, 386, 13],
    [818, 372, 15],
  ] as const) {
    drawSpark(ctx, x, y, r);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function drawSpark(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const rad = i % 2 === 0 ? r : r * 0.35;
    const px = Math.cos(a) * rad;
    const py = Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
